import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  LinearProgress,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Toolbar,
  Snackbar,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Popover,
  Tooltip
} from '@mui/material';
import { ViewColumn } from '@mui/icons-material';
import { DataGrid, GridColDef, GridRowSelectionModel, GridValueGetterParams } from '@mui/x-data-grid';
import { useParams, Link } from 'react-router-dom';
import { format, parseISO, subDays, differenceInMinutes } from 'date-fns';
import api from '../services/api';
import DateRangeSelector, { type DateRangeValue } from '../components/DateRangeSelector';

function formatDateTime(iso: string): string {
  if (!iso) return '';
  try {
    return format(parseISO(iso), 'MMM d, yyyy HH:mm');
  } catch {
    return iso || '';
  }
}

function formatDate(iso: string): string {
  if (!iso) return '';
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso || '';
  }
}

/** Compute hours from start/stop (same as table view); API hours may be Decimal and serialize oddly. */
function getDurationHours(startTime?: string, stopTime?: string): number {
  if (!startTime || !stopTime) return 0;
  try {
    const start = parseISO(startTime);
    const stop = parseISO(stopTime);
    return differenceInMinutes(stop, start) / 60;
  } catch {
    return 0;
  }
}

function parseHoursValue(h: unknown): number | null {
  if (h == null || h === '') return null;
  if (typeof h === 'number' && Number.isFinite(h)) return h;
  if (typeof h === 'string') {
    const n = Number(h);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof h === 'object' && h !== null && 'toString' in h) {
    const n = Number((h as { toString(): string }).toString());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected'
};

interface StatusHistoryItem {
  id?: string;
  displayText?: string;
  changedAt?: string;
  comments?: string | null;
}

interface Entry {
  id: string;
  organizationId: string;
  userId: string;
  taskId: string;
  startTime?: string;
  stopTime?: string;
  hours?: number | string;
  description?: string;
  status?: string;
  statusChangedBy?: string;
  statusChangedAt?: string | null;
  statusComments?: string | null;
  user?: { id: string; displayName?: string };
  task?: { id: string; name: string; account?: { id: string; name: string; company?: { id: string; name: string } } };
  organization?: { id: string; name: string };
  statusChangedByUser?: { id: string; displayName?: string | null };
  statusHistory?: StatusHistoryItem[];
}

const Approvals: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const [org, setOrg] = useState<{ id: string; name?: string; timesheetApprovalsEnabled?: boolean } | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const defaultDateRange: DateRangeValue = useMemo(() => {
    const to = new Date();
    const from = subDays(to, 14);
    return { dateFrom: format(from, 'yyyy-MM-dd'), dateTo: format(to, 'yyyy-MM-dd') };
  }, []);
  const [dateRange, setDateRange] = useState<DateRangeValue>(defaultDateRange);
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [columnAnchor, setColumnAnchor] = useState<HTMLElement | null>(null);
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    action: 'submit' | 'return' | 'approve' | 'reject';
    entryIds: string[];
    isBulk: boolean;
  } | null>(null);
  const [actionComment, setActionComment] = useState('');
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({
    user: true,
    task: true,
    company: true,
    account: true,
    description: true,
    startTime: true,
    stopTime: true,
    hours: true,
    status: true,
    approvalDetails: true,
    actions: true
  });

  const loadOrg = useCallback(async () => {
    if (!organizationId) return;
    try {
      const res = await api.getOrganization(organizationId);
      const data = res?.data ?? res;
      setOrg(data ? { id: data.id || organizationId, name: data.name, timesheetApprovalsEnabled: !!data.timesheetApprovalsEnabled } : null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load organization');
    }
  }, [organizationId]);

  const loadEntries = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {
        organizationId,
        startDate: dateRange.dateFrom,
        endDate: dateRange.dateTo,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        includeStatusHistory: 'true'
      };
      if (statusFilter) params.status = statusFilter;
      const response = await api.getTimesheetEntries(params);
      const data = (response.data ?? response) as Entry[];
      const sorted = [...data].sort((a, b) => {
        const tA = a.startTime ? new Date(a.startTime).getTime() : 0;
        const tB = b.startTime ? new Date(b.startTime).getTime() : 0;
        return tB - tA;
      });
      setEntries(sorted);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load entries');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId, dateRange.dateFrom, dateRange.dateTo, statusFilter]);

  useEffect(() => {
    loadOrg();
  }, [loadOrg]);

  useEffect(() => {
    if (organizationId) loadEntries();
  }, [organizationId, loadEntries]);

  const getStatus = (row: Entry) => row.status ?? 'draft';
  const selectedIds = selection.filter((id): id is string => typeof id === 'string');
  const selectedEntries = entries.filter((e) => selectedIds.includes(e.id));

  // Check if selected entries have mixed statuses
  const hasMixedStatuses = useMemo(() => {
    if (selectedEntries.length === 0) return false;
    const statuses = new Set(selectedEntries.map(e => getStatus(e)));
    return statuses.size > 1;
  }, [selectedEntries]);

  const allSelectedHaveStatus = useCallback(
    (status: string) => {
      if (selectedEntries.length === 0) return false;
      return selectedEntries.every((e) => getStatus(e) === status);
    },
    [selectedEntries]
  );

  // Determine if each action button should be enabled
  const canSubmit = useMemo(() => {
    if (selectedEntries.length === 0 || hasMixedStatuses) return false;
    return allSelectedHaveStatus('draft');
  }, [selectedEntries.length, hasMixedStatuses, allSelectedHaveStatus]);

  const canApprove = useMemo(() => {
    if (selectedEntries.length === 0 || hasMixedStatuses) return false;
    return allSelectedHaveStatus('submitted');
  }, [selectedEntries.length, hasMixedStatuses, allSelectedHaveStatus]);

  const canReject = useMemo(() => {
    if (selectedEntries.length === 0 || hasMixedStatuses) return false;
    return allSelectedHaveStatus('submitted');
  }, [selectedEntries.length, hasMixedStatuses, allSelectedHaveStatus]);

  const canReturn = useMemo(() => {
    if (selectedEntries.length === 0 || hasMixedStatuses) return false;
    const statuses = selectedEntries.map(e => getStatus(e));
    // Return is valid for submitted, approved, or rejected (not draft)
    return statuses.every(s => ['submitted', 'approved', 'rejected'].includes(s));
  }, [selectedEntries.length, hasMixedStatuses, selectedEntries]);

  const runBulkAction = async (
    action: 'submit' | 'return' | 'approve' | 'reject',
    entryIds: string[],
    comment: string
  ) => {
    setActionLoading(true);
    setSnack(null);
    try {
      let res: any;
      if (action === 'submit') res = await api.bulkSubmitTimesheetEntries({ entryIds, comments: comment || undefined });
      else if (action === 'return') res = await api.bulkReturnTimesheetEntries({ entryIds, reason: comment || undefined });
      else if (action === 'approve') res = await api.bulkApproveTimesheetEntries({ entryIds, comments: comment || undefined });
      else res = await api.bulkRejectTimesheetEntries({ entryIds, reason: comment || undefined });
      const data = res?.data ?? res;
      const succeeded = data?.totalSucceeded ?? (data?.results?.filter((r: any) => r.success).length ?? 0);
      const failed = data?.totalFailed ?? (data?.results?.filter((r: any) => !r.success).length ?? 0);
      setSnack({
        message: failed > 0 ? `${succeeded} succeeded, ${failed} failed` : `${action} completed successfully`,
        severity: failed > 0 ? 'error' : 'success'
      });
      setSelection([]);
      await loadEntries();
    } catch (err: any) {
      setSnack({ message: err.response?.data?.message || `Failed to ${action}`, severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const runRowAction = async (id: string, action: 'submit' | 'return' | 'approve' | 'reject', comment: string) => {
    setActionLoading(true);
    setSnack(null);
    try {
      if (action === 'submit') await api.submitTimesheetEntry(id, { comments: comment || undefined });
      else if (action === 'return') await api.returnTimesheetEntry(id, { reason: comment || undefined });
      else if (action === 'approve') await api.approveTimesheetEntry(id, { comments: comment || undefined });
      else await api.rejectTimesheetEntry(id, { reason: comment || undefined });
      setSnack({ message: `${action} completed`, severity: 'success' });
      await loadEntries();
    } catch (err: any) {
      setSnack({ message: err.response?.data?.message || `Failed to ${action}`, severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmAction = async () => {
    if (!actionDialog) return;
    const { action, entryIds, isBulk } = actionDialog;
    const comment = actionComment.trim();
    setActionDialog(null);
    setActionComment('');
    if (isBulk) await runBulkAction(action, entryIds, comment);
    else await runRowAction(entryIds[0], action, comment);
  };

  const actionDialogTitle = actionDialog
    ? actionDialog.isBulk
      ? `${actionDialog.action.charAt(0).toUpperCase() + actionDialog.action.slice(1)} selected (${actionDialog.entryIds.length})`
      : `${actionDialog.action.charAt(0).toUpperCase() + actionDialog.action.slice(1)} entry`
    : '';
  const commentLabel =
    actionDialog?.action === 'reject' || actionDialog?.action === 'return'
      ? 'Reason (optional)'
      : 'Comment (optional)';

  const allColumnDefs: GridColDef<Entry>[] = useMemo(() => [
    {
      field: 'user',
      headerName: 'User',
      width: 140,
      resizable: true,
      valueGetter: (params: GridValueGetterParams<Entry>) => params.row?.user?.displayName ?? params.row?.userId ?? '—'
    },
    {
      field: 'company',
      headerName: 'Company',
      width: 130,
      resizable: true,
      valueGetter: (params: GridValueGetterParams<Entry>) => params.row?.task?.account?.company?.name ?? '—'
    },
    {
      field: 'account',
      headerName: 'Account',
      width: 130,
      resizable: true,
      valueGetter: (params: GridValueGetterParams<Entry>) => params.row?.task?.account?.name ?? '—'
    },
    {
      field: 'task',
      headerName: 'Task',
      width: 160,
      resizable: true,
      valueGetter: (params: GridValueGetterParams<Entry>) => params.row?.task?.name ?? '—'
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 180,
      resizable: true,
      valueGetter: (params: GridValueGetterParams<Entry>) => (params.row?.description ?? '') || '—'
    },
    {
      field: 'startTime',
      headerName: 'Start',
      width: 150,
      resizable: true,
      valueGetter: (params: GridValueGetterParams<Entry>) => formatDateTime(params.row?.startTime || '')
    },
    {
      field: 'stopTime',
      headerName: 'Stop',
      width: 150,
      resizable: true,
      valueGetter: (params: GridValueGetterParams<Entry>) => formatDateTime(params.row?.stopTime || '')
    },
    {
      field: 'hours',
      headerName: 'Hours',
      width: 90,
      resizable: true,
      valueGetter: (params: GridValueGetterParams<Entry>) => {
        const row = params.row;
        const fromField = parseHoursValue(row?.hours);
        const hours = fromField ?? getDurationHours(row?.startTime, row?.stopTime);
        return Number.isFinite(hours) ? hours.toFixed(1) : '—';
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      resizable: true,
      renderCell: (params) => {
        const s = getStatus(params.row);
        return (
          <Chip
            size="small"
            label={STATUS_LABELS[s] ?? s}
            color={s === 'approved' ? 'success' : s === 'rejected' ? 'error' : s === 'submitted' ? 'warning' : 'default'}
          />
        );
      }
    },
    {
      field: 'approvalDetails',
      headerName: 'Approval details',
      minWidth: 200,
      width: 220,
      flex: 1,
      resizable: true,
      valueGetter: (params: GridValueGetterParams<Entry>) => {
        const row = params.row;
        const u = row?.statusChangedByUser?.displayName;
        const at = row?.statusChangedAt ? formatDate(row.statusChangedAt) : '';
        const c = (row?.statusComments || '').trim();
        const commentShort = c.length > 50 ? c.slice(0, 50) + '…' : c;
        if (!u && !at && !c) return '—';
        return [u, at, commentShort].filter(Boolean).join(' – ');
      },
      renderCell: (params) => {
        const row = params.row;
        const latest = (() => {
          const u = row?.statusChangedByUser?.displayName;
          const at = row?.statusChangedAt ? formatDate(row.statusChangedAt) : '';
          const c = (row?.statusComments || '').trim();
          const commentShort = c.length > 50 ? c.slice(0, 50) + '…' : c;
          if (!u && !at && !c) return '—';
          return [u, at, commentShort].filter(Boolean).join(' – ');
        })();
        const history = row?.statusHistory;
        const hasHistory = Array.isArray(history) && history.length > 0;
        const tooltipContent = hasHistory
          ? history.map((h) => h.displayText || '').filter(Boolean).join('\n')
          : latest !== '—' ? latest : 'No history';
        return (
          <Tooltip
            title={
              <Box component="span" sx={{ whiteSpace: 'pre-wrap', display: 'block', maxHeight: 320, overflow: 'auto' }}>
                {tooltipContent}
              </Box>
            }
            placement="top-start"
            enterDelay={300}
          >
            <Box component="span" sx={{ cursor: hasHistory ? 'help' : 'default' }}>
              {latest}
            </Box>
          </Tooltip>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 320,
      resizable: true,
      sortable: false,
      renderCell: (params) => {
        const s = getStatus(params.row);
        const openDialog = (action: 'submit' | 'return' | 'approve' | 'reject') => {
          setActionDialog({ open: true, action, entryIds: [params.row.id], isBulk: false });
          setActionComment('');
        };
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {s === 'draft' && (
              <Button size="small" variant="outlined" onClick={() => openDialog('submit')} disabled={actionLoading}>
                Submit
              </Button>
            )}
            {s === 'submitted' && (
              <>
                <Button size="small" variant="outlined" color="success" onClick={() => openDialog('approve')} disabled={actionLoading}>
                  Approve
                </Button>
                <Button size="small" variant="outlined" color="error" onClick={() => openDialog('reject')} disabled={actionLoading}>
                  Reject
                </Button>
              </>
            )}
            {(s === 'submitted' || s === 'approved' || s === 'rejected') && (
              <Button size="small" variant="outlined" onClick={() => openDialog('return')} disabled={actionLoading}>
                Return
              </Button>
            )}
          </Box>
        );
      }
    }
  ], [actionLoading]);

  const columns = useMemo(() => allColumnDefs.filter((col) => visibleColumns[col.field] !== false), [allColumnDefs, visibleColumns]);

  if (!organizationId) {
    return (
      <Alert severity="info">Select an organization.</Alert>
    );
  }

  if (org && !org.timesheetApprovalsEnabled) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom>Approvals</Typography>
        <Alert severity="info">
          Timesheet approvals are disabled for this organization. Enable them in{' '}
          <Button component={Link} to={`/org/${organizationId}/settings`} size="small">Organization settings</Button>.
        </Alert>
      </Box>
    );
  }

  const columnOptions = [
    { field: 'user', label: 'User' },
    { field: 'company', label: 'Company' },
    { field: 'account', label: 'Account' },
    { field: 'task', label: 'Task' },
    { field: 'description', label: 'Description' },
    { field: 'startTime', label: 'Start' },
    { field: 'stopTime', label: 'Stop' },
    { field: 'hours', label: 'Hours' },
    { field: 'status', label: 'Status' },
    { field: 'approvalDetails', label: 'Approval details' },
    { field: 'actions', label: 'Actions' }
  ];

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Typography variant="h5" gutterBottom>Approvals</Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="submitted">Submitted</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
        <DateRangeSelector value={dateRange} onChange={setDateRange} size="small" />
        <Button variant="outlined" size="small" onClick={() => loadEntries()}>Refresh</Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ViewColumn />}
          onClick={(e) => setColumnAnchor(e.currentTarget)}
          aria-describedby="column-selector"
        >
          Columns
        </Button>
        <Popover
          id="column-selector"
          open={Boolean(columnAnchor)}
          anchorEl={columnAnchor}
          onClose={() => setColumnAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Box sx={{ p: 2, minWidth: 180 }}>
            <Typography variant="subtitle2" gutterBottom>Show columns</Typography>
            <FormGroup>
              {columnOptions.map(({ field, label }) => (
                <FormControlLabel
                  key={field}
                  control={
                    <Checkbox
                      checked={visibleColumns[field] !== false}
                      onChange={(e) => setVisibleColumns((prev) => ({ ...prev, [field]: e.target.checked }))}
                    />
                  }
                  label={label}
                />
              ))}
            </FormGroup>
          </Box>
        </Popover>
      </Box>

      <Toolbar variant="dense" sx={{ gap: 1, flexWrap: 'wrap', bgcolor: 'grey.100', borderRadius: 1, mb: 1, minHeight: 48 }}>
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {selectedIds.length > 0 ? `${selectedIds.length} selected${hasMixedStatuses ? ' (mixed statuses)' : ''}` : 'No selection'}
        </Typography>
        <Button size="small" variant="outlined" color="inherit" disabled={actionLoading || !canSubmit} onClick={() => { setActionDialog({ open: true, action: 'submit', entryIds: selectedIds, isBulk: true }); setActionComment(''); }} sx={{ borderColor: 'grey.400', color: 'grey.700' }}>
          Submit selected
        </Button>
        <Button size="small" variant="outlined" color="success" disabled={actionLoading || !canApprove} onClick={() => { setActionDialog({ open: true, action: 'approve', entryIds: selectedIds, isBulk: true }); setActionComment(''); }}>
          Approve selected
        </Button>
        <Button size="small" variant="outlined" color="error" disabled={actionLoading || !canReject} onClick={() => { setActionDialog({ open: true, action: 'reject', entryIds: selectedIds, isBulk: true }); setActionComment(''); }}>
          Reject selected
        </Button>
        <Button size="small" variant="outlined" color="inherit" disabled={actionLoading || !canReturn} onClick={() => { setActionDialog({ open: true, action: 'return', entryIds: selectedIds, isBulk: true }); setActionComment(''); }} sx={{ borderColor: 'grey.400', color: 'grey.700' }}>
          Return selected
        </Button>
      </Toolbar>

      <Dialog open={!!actionDialog} onClose={() => { setActionDialog(null); setActionComment(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>{actionDialogTitle}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={commentLabel}
            fullWidth
            variant="outlined"
            multiline
            minRows={2}
            value={actionComment}
            onChange={(e) => setActionComment(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleConfirmAction(); } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setActionDialog(null); setActionComment(''); }}>Cancel</Button>
          <Button onClick={handleConfirmAction} variant="contained" color="primary" disabled={actionLoading}>
            {actionLoading ? 'Working…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <Box sx={{ overflowX: 'auto', minHeight: 400 }}>
        <DataGrid
          rows={entries}
          columns={columns}
          getRowId={(row) => row.id}
          checkboxSelection
          rowSelectionModel={selection}
          onRowSelectionModelChange={(newSelection) => setSelection(newSelection)}
          disableRowSelectionOnClick={false}
          autoHeight
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{ minWidth: 800, minHeight: 400 }}
          columnHeaderHeight={40}
          disableColumnMenu={false}
        />
      </Box>

      <Snackbar open={!!snack} autoHideDuration={6000} onClose={() => setSnack(null)}>
        <Alert severity={snack?.severity ?? 'info'} onClose={() => setSnack(null)} sx={{ width: '100%' }}>
          {snack?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Approvals;
